/* eslint-disable react/no-danger */

import * as React from 'react';

import { Button } from '@ao-internal/components-react';

import { ChatMessageTypes, ChatMessageTypesClassMap } from '../../store';
import useDrawerStore from '../../store/drawer';
import ArticleCard, { Article } from './ArticleCard';

export type ArticleMessageContent = Article;
export interface ArticlesMessageProps {
	articles: ArticleMessageContent[];
	numberOfArticlesToShow?: number;
}

export const dataTestId = `${
	ChatMessageTypesClassMap[ChatMessageTypes.Articles]
}-message`;

export function createAnalyticsTagsForForArticleLinks(
	target: HTMLElement
): HTMLElement {
	// Add analytics tagging attributes to all anchor links within the article content
	target.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
		anchor.setAttribute('data-tag-name', 'article link');
		anchor.setAttribute('data-tag-link-text', anchor.innerText);
		anchor.setAttribute('data-tag-href', anchor.href);
	});

	return target;
}

export const ArticlesCard: React.FC<ArticlesMessageProps> = ({
	articles,
	numberOfArticlesToShow = 2,
}) => {
	const articleBodyRef = React.useRef<React.ReactNode>(null);
	const drawerStore = useDrawerStore();

	const [articleCards, setArticleCards] = React.useState<
		ArticleMessageContent[]
	>(articles.slice(0, numberOfArticlesToShow));

	const [showViewAllCta, setShowViewAllCta] = React.useState(
		articles.length > numberOfArticlesToShow
	);

	function handleViewAllClick() {
		setArticleCards(articles);
		setShowViewAllCta(false);
	}

	const onRefChange = React.useCallback((node: React.ReactNode) => {
		articleBodyRef.current = node;

		if (articleBodyRef.current) {
			articleBodyRef.current = createAnalyticsTagsForForArticleLinks(
				articleBodyRef.current as HTMLElement
			);
		}
	}, []);

	function handleReadMoreClick(articleIndex: number) {
		const article = articles[articleIndex];

		drawerStore.setOnClose(() => {
			// TODO: Add analytics
			drawerStore.setIsOpen(false);
		});
		drawerStore.setOptions({
			'data-tag-area': 'article slideout',
			'data-tag-article-title': article.title,
			'aria-labelledby': 'chatbot-drawer-article-title',
			elementToReturnFocusOnClose: document.activeElement
				? (document.activeElement as HTMLElement)
				: undefined,
			// TODO: Remove type casting. It doesn't like the data attributes
		} as React.HTMLProps<HTMLDivElement>);
		drawerStore.setContent(() => (
			<React.Fragment>
				<h3 id="chatbot-drawer-article-title" className="text-title">
					{article.title}
				</h3>
				<div
					className="prose-sm prose"
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{ __html: article.body }}
					ref={onRefChange}
				/>
			</React.Fragment>
		));
		drawerStore.setIsOpen(true);
	}

	return (
		<div
			data-testid={dataTestId}
			data-tag-area="articles message"
			className="mx-2 mb-2"
		>
			{articleCards.map((article, index) => (
				<ArticleCard
					// eslint-disable-next-line react/no-array-index-key
					key={`article-card-${index}`}
					data-testid="article-card"
					data-tag-article-title={article.title}
					data-tag-position={index + 1}
					article={article}
					onReadMore={() => handleReadMoreClick(index)}
				/>
			))}

			{showViewAllCta && (
				<Button
					variant="secondary"
					data-testid="view-all-cta"
					data-tag-name="view all cta"
					type="button"
					onClick={() => handleViewAllClick()}
					className="hover:bg-cta-actionhover hover:text-white justify-center w-full px-0 text-center whitespace-normal bg-white"
				>
					View all ({articles.length - numberOfArticlesToShow})
				</Button>
			)}
		</div>
	);
};

export default ArticlesCard;
